import React, { FC } from 'react';
import { Button, List, Tooltip } from 'antd';
import { CloseOutlined, ExportOutlined } from '@ant-design/icons';
import OWLink from '../OWLink';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

// Location columns are the buyer view, where the calendar is labelled "Store Calendar".
const BACK_TEXT = 'Back to store calendar';

interface LocationIdentityProps {
	location: any;
	nameSuffix?: React.ReactNode;
	/** Matches TechnicianIdentity: the column header row wants `flex-1`, the week-view nav `w-full`. */
	widthClassName?: string;
}

export const LocationIdentity: FC<LocationIdentityProps> = ({
	location,
	nameSuffix,
	widthClassName = 'flex-1',
}) => {
	const name = nullSafeGetOrElse('name', location, 'Location');
	const workOrderCount = location ? location.workOrderCount : undefined;
	return (
		<div className={`flex min-w-0 flex-row items-center ${widthClassName}`}>
			<List.Item.Meta
				className="min-w-0 flex-1 items-center [&_.ant-list-item-meta-content]:min-w-0"
				title={
					<span className="flex min-w-0 max-w-full flex-row items-start gap-1">
						<Tooltip title={name} placement="top">
							<span className="line-clamp-2 min-w-0">{name}</span>
						</Tooltip>
						{nameSuffix}
					</span>
				}
				description={
					typeof workOrderCount === 'number' ? (
						<span className="block max-w-full truncate">
							{workOrderCount} WO{workOrderCount > 1 && 's'}
						</span>
					) : undefined
				}
			/>
		</div>
	);
};

interface LocationColumnHeaderProps {
	location: any;
	onRemove: (locationId: any) => void;
}

export const LocationColumnHeader: FC<LocationColumnHeaderProps> = ({ location, onRemove }) => {
	if (!location) {
		return null;
	}
	const locationId = nullSafeGet('id', location);
	const detailPath = `/buyer/locations/detail/${locationId}/details`;

	const handleRemove = () => onRemove(locationId);

	return (
		<div className="technicianColumnHeader flex h-full w-full flex-row items-center gap-2 py-2 pl-3 pr-1 text-left">
			<LocationIdentity
				location={location}
				nameSuffix={
					<OWLink
						to={detailPath}
						target="_blank"
						preserveBackLinkStates={true}
						backText={BACK_TEXT}
						className="flex-none"
					>
						<ExportOutlined className="text-buyer" />
					</OWLink>
				}
			/>
			<Button
				type="text"
				size="small"
				className="flex-none self-start"
				icon={<CloseOutlined />}
				onClick={handleRemove}
			/>
		</div>
	);
};
